import InfoContext from "pages/InfoContext";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import ToggleButton from "core/buttons/toggle-button/ToggleButton";
import { Controller } from "utils/Controller";
import { FormattedMessage } from "react-intl";
import { QUERY_STATUS, TAG_TYPES } from "constants/constants";
import { Typography } from "core/typography/Typography";
import { useContext, useEffect } from "react";
import { useDybContext } from "api/external-apis/dyb/DybProvider";
import {
  CONSULTANT_FORM_LABELS,
  CONSULTANT_FORM_PLACEHOLDERS,
  FORM_INFO,
  ROLE_CSE,
  ROLE_PLPM,
} from "constants/forms";
import {
  formatNamesOptions,
  renderBirthday,
  renderFirstName,
  renderInputEmail,
  renderInputGender,
  renderLastName,
} from "../CommonInputs";
import propTypes from "prop-types";
import { updateUserValues } from "utils/consultantHelper";
import { buildDybProfileUrl, getErrorCellClass } from "utils/helper";

const DoyoubuzzForm = ({
  control,
  errors,
  readOnly,
  register,
  roles,
  setRoles,
  setValue,
  watch,
  cellHighlight,
}) => {
  const {
    getPersonsByName,
    usersByName,
    id,
    lang,
    consultantBySlug,
    editConsultantStatus,
    setStatus,
    setCode,
    slug,
    apiErrorResponse,
  } = useContext(InfoContext);
  const { loadDybList } = useDybContext();
  const personal_email = watch("personal_email");
  const switchRole = (role) => {
    if (roles?.includes(role)) {
      setRoles(roles.filter((elm) => elm !== role));
    } else {
      setRoles([...roles, role]);
    }
  };
  const dyb_showcase_id = watch(
    "dyb_showcase_id",
    id ? [{ value: consultantBySlug.dyb_showcase_id }] : []
  );

  const selectedDybPerson = usersByName?.find(
    (elm) => elm.id === dyb_showcase_id[0]?.value
  );

  useEffect(() => {
    editConsultantStatus === QUERY_STATUS.INITIAL &&
      selectedDybPerson &&
      updateUserValues(selectedDybPerson, setValue);
  }, [selectedDybPerson]);

  const renderDYB = () => (
    <Controller
      {...{
        control,
        register,
        name: "dyb_showcase_id",
        rules: {
          required: true,
        },
        render: (props) => (
          <InputSearchTag
            errors={errors["dyb_showcase_id"]}
            getOptions={getPersonsByName}
            label={{ id: "dyb_name_label", defaultMessage: "Recherche DYB" }}
            limitChoice={1}
            minLength={2}
            onDelete={() => setValue("dyb_showcase_id", [])}
            onTagClick={
              dyb_showcase_id[0]?.value
                ? (option) =>
                    window.open(
                      buildDybProfileUrl(option.value),
                      "_blank",
                      "noopener,noreferrer"
                    )
                : undefined
            }
            options={formatNamesOptions({ list: usersByName })}
            placeholder={CONSULTANT_FORM_PLACEHOLDERS.dyb_showcase_id}
            popup={FORM_INFO.candidate.popup}
            readOnly={readOnly}
            required
            selectedOptions={dyb_showcase_id}
            setValue={(val) => setValue("dyb_showcase_id", val)}
            type={TAG_TYPES.PEOPLE}
            loadDybList={loadDybList}
            id="dyb"
            {...props}
          />
        ),
      }}
    />
  );
  const formFieldsLine1 = [
    {
      render: renderInputGender,
      label: CONSULTANT_FORM_LABELS.gender,
      placeholder: CONSULTANT_FORM_PLACEHOLDERS.placeholder,
      readOnly,
      id: "gender",
      register,
      setValue,
    },
    {
      render: renderLastName,
      label: CONSULTANT_FORM_LABELS.last_name,
      placeholder: CONSULTANT_FORM_PLACEHOLDERS.last_name,
      readOnly,
      id: "last_name",
      register,
    },
    {
      render: renderFirstName,
      label: CONSULTANT_FORM_LABELS.first_name,
      placeholder: CONSULTANT_FORM_PLACEHOLDERS.first_name,
      id: "first_name",
      readOnly,
      register,
    },
  ];

  const formFiledsLine2 = [
    {
      render: renderInputEmail,
      label: CONSULTANT_FORM_LABELS.personal_email,
      placeholder: CONSULTANT_FORM_PLACEHOLDERS.personal_email,
      readOnly,
      id: "personal_email",
      register,
      name: "personal_email",
      errorMessage:
        apiErrorResponse?.["email"] ||
        (personal_email && errors?.["personal_email"]?.message),
      copyEmail: slug,
      setCode,
      setStatus,
    },
    {
      render: renderBirthday,
      label: CONSULTANT_FORM_LABELS.birthday,
      placeholder: CONSULTANT_FORM_PLACEHOLDERS.birthday,
      readOnly,
      id: "birthday",
      register,
      setValue,
      lang,
    },
  ];

  return (
    <div className="doyoubuzz-section">
      <div className="doyoubuzz-title">
        <Typography
          className="title-3 violet text-uppercase"
          type="h2"
          label={<FormattedMessage id="doyoubuzz" defaultMessage="doyoubuzz" />}
        />
        {!id && (
          <div className="doyoubuzz-toggles">
            <ToggleButton
              id="cse"
              label="Membre du CSE"
              readOnly={readOnly}
              onChange={() => switchRole(ROLE_CSE)}
              value={roles?.includes(ROLE_CSE)}
            />
            <ToggleButton
              id="leader"
              label="Practice leader"
              onChange={() => switchRole(ROLE_PLPM)}
              readOnly={readOnly}
              value={roles?.includes(ROLE_PLPM)}
            />
          </div>
        )}
      </div>
      <div className="doyoubuzz-form">
        <div
          className={`cell-padding ${
            watch("dyb_showcase_id").length == 0 &&
            cellHighlight &&
            "errorCellColor"
          }`}
        >
          {renderDYB()}
        </div>
        {formFieldsLine1.map((field, index) => (
          <div
            key={index}
            className={`cell-padding ${getErrorCellClass(
              field,
              errors,
              watch,
              cellHighlight
            )}`}
          >
            {field.render({
              control,
              errors,
              label: field.label,
              placeholder: field.placeholder,
              readOnly,
              register,
              setValue,
              value: watch(field.id),
              ...field,
            })}
          </div>
        ))}
        {formFiledsLine2.map((field, index) => (
          <div
            key={index}
            className={`cell-padding ${
              !watch(field?.id) && cellHighlight && "errorCellColor"
            }`}
          >
            {field.render({
              control,
              errors,
              label: field.label,
              placeholder: field.placeholder,
              readOnly,
              register,
              setValue,
              value: watch(field.id),
              ...field,
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DoyoubuzzForm;

DoyoubuzzForm.propTypes = {
  control: propTypes.object.isRequired,
  errors: propTypes.object.isRequired,
  readOnly: propTypes.bool,
  register: propTypes.func.isRequired,
  roles: propTypes.array.isRequired,
  setRoles: propTypes.func.isRequired,
  setValue: propTypes.func.isRequired,
  watch: propTypes.func.isRequired,
  cellHighlight: propTypes.bool,
};
